﻿import { getTranslations } from "../translations";

export enum PopupPosition
{
    // Desktop only
    Left = "left",
    Right = "right",
    Center = "center",
    CenterSmall = "centersmall",

    // Mobile only
    Drawer = "drawer",
    Popup = "Popup",
    fullScreen = "fullscreen"
}

class PopupOptions
{
    id?: string | null;
    popupClass?: string | null;
    showLogo?: boolean = false; // show logo OR title
    logoCentered?: boolean = false;
    headerIcon?: string | null;
    title?: string | null;
    subtitle?: string | null;
    content: string | HTMLElement | Array<PopupContent> | Array<PopupContentOptions>;
    footerContent?: string | HTMLElement | null;
    buttons?: Array<PopupButton> | null;
    backButton?: PopupButton | null;
    closeButton?: boolean | PopupButton | null = false;
    desktopPosition?: PopupPosition = PopupPosition.Right;
    mobilePosition?: PopupPosition = PopupPosition.Popup;
    customButtonContainer?: string | null;
    grid?: boolean = false; // show grid OR list
    onShow?: () => void | null;
    onHide?: () => void | null;
    onChange?: (event: Event) => void | null;
}

export class PopupButton
{
    className?: string | null;
    label: string;
    url?: string | null;
    onClick?: (event: Event) => void | null;
    closeOnCLick?: boolean = true;
    isNextButton?: boolean = false;
    isAnchor?: boolean = false;
}

export class PopupContent
{
    typestring?: string = "PopupContent";
    index?: number = 0;
    title?: string | null;
    subtitle?: string | null;
    content: string | HTMLElement;
}

export class PopupContentOptions
{
    constructor(id: string, checked?: boolean)
    {
        this.id = id;
        this.checked = checked;
    }

    id: string;
    name?: string;
    value?: string;
    text?: string;
    checked: boolean;
    disabled?: boolean;
    image?: string;
}

export class Popup
{
    private root: HTMLElement;
    private overlay: HTMLElement;
    private container: HTMLElement;
    private options: PopupOptions;
    public activeTab: number;
    private contentInner!: HTMLElement;
    private contentHolder!: HTMLElement;
    private buttonHolder: HTMLElement;
    private backButtonEl: HTMLElement | null = null;
    private nextButtonEl: HTMLButtonElement | null = null;
    private _buttons: Array<PopupButton> | null = null;
    private closeButton: HTMLElement;
    private lastFocusedElement: HTMLElement | null = null;
    private focusableElements: NodeListOf<HTMLElement>;
    private titleID: string | null;
    private scrollTopEl: HTMLElement;

    get buttons()
    {
        return this._buttons;
    }

    set buttons(newButtons: Array<PopupButton>)
    {
        // Only change when buttons are changed
        if (newButtons !== this._buttons)
        {
            this._buttons = newButtons;
            this.updateButtonsUI();
        }
    }

    constructor(options: PopupOptions)
    {
        this.options = options;
        this.activeTab = 0;

        this.root = document.createElement("div");
        this.root.classList.add("g-popup");

        this.root.setAttribute("role", "dialog");
        this.root.setAttribute("aria-modal", "true");

        if (options.id)
        {
            this.root.id = "modal-" + options.id;
            document.getElementById(this.root.id)?.remove();
            this.titleID = "modal-title-" + this.root.id;
            this.root.setAttribute("aria-labelledby", this.titleID);
        }

        if (options.popupClass)
        {
            // Add all classes.
            const cleanClasses = options.popupClass.split(",").map(c => c.trim()).filter(Boolean);
            if (cleanClasses.length > 0)
                this.root.classList.add(...cleanClasses);
        }

        if (options.desktopPosition)
        {
            switch (options.desktopPosition)
            {
                case PopupPosition.Left: {
                    this.root.classList.add("g-popup-d--left");
                    break;
                }
                case PopupPosition.Right: {
                    this.root.classList.add("g-popup-d--right");
                    break;
                }
                case PopupPosition.Center: {
                    this.root.classList.add("g-popup-d--center");
                    break;
                }
                case PopupPosition.CenterSmall: {
                    this.root.classList.add("g-popup-d--center--small");
                    break;
                }
            }
        }
        else
            this.root.classList.add("g-popup-d--right");

        if (options.mobilePosition)
        {
            switch (options.mobilePosition)
            {
                case PopupPosition.Drawer: {
                    this.root.classList.add("g-popup-m--drawer");
                    break;
                }
                case PopupPosition.fullScreen: {
                    this.root.classList.add("g-popup-m--fullscreen");
                    break;
                }
                case PopupPosition.Popup:
                default: {
                    // Default
                    this.root.classList.add("g-popup-m--popup");
                    break;
                }
            }
        }

        if (options.buttons)
            this._buttons = options.buttons;

        this.overlay = document.createElement("div");
        this.overlay.classList.add("g-popup__overlay");
        this.overlay.setAttribute("aria-hidden", "true");
        this.overlay.addEventListener("click", () => this.hide());

        this.container = document.createElement("div");
        this.container.classList.add("g-popup__container");

        this.build();
        document.body.appendChild(this.root);

        // Selecteer alle interactieve elementen in de pop-up
        this.focusableElements = this.container.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
    }

    private build()
    {
        const instance = this;
        const headerEl = document.createElement("div");
        headerEl.classList.add("g-popup__header");

        const backButton = this.options.backButton;

        if (backButton)
        {
            if (backButton.isAnchor)
            {
                this.backButtonEl = document.createElement("a");
                this.backButtonEl.setAttribute("href", backButton.url);
            }
            else
                this.backButtonEl = document.createElement("button");

            if (backButton.label)
            {
                this.backButtonEl.innerText = backButton.label;
                this.backButtonEl.title = backButton.label;
            }

            if (backButton.className)
                this.backButtonEl.className = backButton.className;
            else
            {
                this.backButtonEl.classList.add("g-popup__backbutton");
                this.backButtonEl.classList.add("btn-text-link");
            }
            if (backButton.onClick)
                this.backButtonEl.addEventListener("click", backButton.onClick);
            else
                this.backButtonEl.addEventListener("click", () => this.previousTab());

            headerEl.appendChild(this.backButtonEl);
        }

        if (this.options.headerIcon)
        {
            const headerIconEl = document.createElement("i");
            headerIconEl.classList.add("g-popup-header__icon");
            headerIconEl.classList.add("icon-size-m");
            headerIconEl.className += " " + this.options.headerIcon;
            headerEl.appendChild(headerIconEl);
        }

        if (this.options.showLogo)
        {
            const logoEl = document.createElement("div");
            logoEl.classList.add("g-popup__logo");
            headerEl.appendChild(logoEl);

            if (this.options.logoCentered)
                logoEl.classList.add("g-popup__logo--centered")
        }
        else
        {
            const titleHolderEl = document.createElement("div");
            titleHolderEl.classList.add("g-popup__titleholder");

            if (this.options.title)
            {
                const titleEl = document.createElement("span");
                titleEl.className = "g-popup__title";
                titleEl.textContent = this.options.title!;

                if (this.titleID)
                    titleEl.id = this.titleID;

                titleHolderEl.appendChild(titleEl);
            }

            if (this.options.subtitle)
            {
                const subtitleEl = document.createElement("span");
                subtitleEl.className = "g-popup__subtitle";
                subtitleEl.textContent = this.options.subtitle!;
                titleHolderEl.appendChild(subtitleEl);
            }

            if (this.options.title || this.options.subtitle)
                headerEl.appendChild(titleHolderEl);
        }

        if (this.options.closeButton)
        {
            const button = this.options.closeButton;

            const closeButtonEl = document.createElement("button");
            closeButtonEl.className = "btn-icon g-popup__close";
            if (button instanceof PopupButton)
            {
                if (button.onClick)
                {
                    closeButtonEl.addEventListener("click", (e) =>
                    {
                        button.onClick(e);
                        instance.hide();
                    });
                }
                else
                    closeButtonEl.addEventListener("click", () => this.hide());
            }
            else
                closeButtonEl.addEventListener("click", () => this.hide());

            closeButtonEl.setAttribute("aria-label", getTranslations().txtClose);

            headerEl.appendChild(closeButtonEl);
            this.closeButton = closeButtonEl;
        }

        const contentHolderEl = document.createElement("div");
        contentHolderEl.classList.add("g-popup__contentholder");

        const inner = document.createElement("div");
        inner.classList.add("g-popup__contentholder-inner");
        inner.style.display = "flex";
        inner.style.width = "100%";
        inner.style.transition = "transform .4s ease";

        let hasRadioButtons = false;
        if (Array.isArray(this.options.content))
        {
            this.options.content.forEach((item, i) =>
            {
                if (item instanceof PopupContentOptions)
                {
                    hasRadioButtons = true;
                    const itemEl = document.createElement("div");

                    itemEl.className = item.checked ? "c-popup__radio-holder c-popup__radio-holder--active" : "c-popup__radio-holder";

                    const itemRadiobutton = document.createElement("input");
                    itemRadiobutton.className = "u-customradio__input";
                    itemRadiobutton.type = "radio";
                    itemRadiobutton.id = item.id;
                    itemRadiobutton.name = item.name;
                    itemRadiobutton.value = item.value;
                    itemRadiobutton.checked = item.checked;
                    itemRadiobutton.onclick = () => { customRadioClick(itemRadiobutton, "u-customradio__input"); };

                    itemRadiobutton.onchange = (event: Event) =>
                    {
                        instance.options.onChange(event);
                    }

                    const radioButtonLabel = document.createElement("label");
                    radioButtonLabel.className = "u-customradio__label";
                    radioButtonLabel.setAttribute("for", itemRadiobutton.id);
                    radioButtonLabel.innerText = item.text;

                    if (item.image)
                    {
                        const imageEl = document.createElement("img");
                        imageEl.src = item.image;
                        imageEl.alt = item.text;
                        imageEl.className = "u-customradio__image";
                        radioButtonLabel.prepend(imageEl);
                    }

                    const radioButtonText = document.createElement("span");
                    radioButtonText.className = `u-customradio__span`;
                    radioButtonLabel.prepend(radioButtonText);

                    itemEl.appendChild(itemRadiobutton);
                    itemEl.appendChild(radioButtonLabel);

                    inner.appendChild(itemEl);
                }
                else
                {
                    if (typeof item.index !== "number")
                        item.index = i;

                    const contentEl = document.createElement("div");
                    contentEl.classList.add("g-popup__content");
                    contentEl.setAttribute("data-index", item.index.toString());
                    contentEl.style.minWidth = "100%";
                    contentEl.style.boxSizing = "border-box";

                    if (typeof item.content === "string")
                        contentEl.innerHTML = item.content;
                    else if (item.content)
                        contentEl.appendChild(item.content);

                    inner.appendChild(contentEl);
                }
            });

            if (hasRadioButtons)
            {
                if (this.options.grid)
                {
                    inner.classList.add("g-popup__contentholder-inner--list");
                    inner.classList.add("g-popup__contentholder-inner--grid");
                }
                else
                    inner.classList.add("g-popup__contentholder-inner--list");
            }
        }
        else
        {
            const contentEl = document.createElement("div");
            contentEl.classList.add("g-popup__content");
            contentEl.style.minWidth = "100%";
            contentEl.style.boxSizing = "border-box";

            if (typeof this.options.content === "string")
                contentEl.innerHTML = this.options.content;
            else if (this.options.content instanceof HTMLElement)
                contentEl.appendChild(this.options.content);

            inner.appendChild(contentEl);
        }

        this.contentInner = inner;
        contentHolderEl.appendChild(inner);

        this.container.appendChild(headerEl);
        this.container.appendChild(contentHolderEl);

        this.updateButtonsUI();

        this.root.appendChild(this.overlay);
        this.root.appendChild(this.container);

        this.contentHolder = contentHolderEl;

        this.updateSlide(false);
        this.updateUI();
        this.initEvents();
    }

    private updateSlide(animate: boolean = true)
    {
        if (!this.contentInner)
            return;

        if (!animate)
            this.contentInner.style.transition = "none";
        else
            this.contentInner.style.transition = "transform .4s ease";

        const offset = this.activeTab * -100;
        this.contentInner.style.transform = `translateX(${offset}%)`;

        if (!animate)
        {
            void this.contentInner.offsetWidth;
            this.contentInner.style.transition = "transform .4s ease";
        }
    }

    private updateUI()
    {
        const total = Array.isArray(this.options.content)
            ? this.options.content.length
            : 1;

        // Hide next button when only 1 slide visible
        if (this.nextButtonEl)
        {
            if (total <= 1)
                this.nextButtonEl.classList.add("is-hidden");
            else if (this.activeTab >= total - 1)
                this.nextButtonEl.classList.add("is-hidden");
            else
                this.nextButtonEl.classList.remove("is-hidden");
        }

        if (this.backButtonEl)
        {
            if (total <= 1)
                this.backButtonEl.classList.remove("is-visible");
            else if (this.activeTab < 1)
                this.backButtonEl.classList.remove("is-visible");
            else
                this.backButtonEl.classList.add("is-visible");
        }

        this.contentHolder.scrollTop = 0;
    }

    public show(): Popup
    {
        this.root.classList.add("g-popup--is-active");
        setTimeout(() =>
        {
            this.root.classList.add("g-popup--visible");
            document.documentElement.classList.add("u-popup--active");
            if (this.options.onShow)
                this.options.onShow();

            // Aria
            this.lastFocusedElement = document.activeElement as HTMLElement;
            this.container.setAttribute('aria-hidden', 'false');

            this.container.focus();

            if (this.scrollTopEl)
            {
                if (this.contentHolder.scrollHeight > this.contentHolder.clientHeight)
                    this.scrollTopEl.classList.add("is-visible");
                else
                    this.scrollTopEl.classList.remove("is-visible");
            }
        }, 100);
        return this;
    }

    public hide()
    {
        this.root.classList.remove("g-popup--visible");
        document.documentElement.classList.remove("u-popup--active");

        this.reset();

        if (this.options.onHide)
            this.options.onHide();

        // Aria
        this.container.setAttribute('aria-hidden', 'true');
        // Reset focus
        if (this.lastFocusedElement)
        {
            this.lastFocusedElement.focus();
        }
        setTimeout(() =>
        {
            this.root.classList.remove("g-popup--is-active");
        }, 100);
    }

    private reset()
    {
        this.activeTab = 0;
    }

    public updateFooter(footerContent: any, buttons: PopupButton[])
    {
        if (footerContent === "" || footerContent === undefined || footerContent === null)
        {
            this.options.footerContent = null;
        } else
        {
            this.options.footerContent = footerContent;
        }

        this.buttons = buttons;
        this.updateButtonsUI();
    }

    private updateButtonsUI()
    {
        const instance = this;
        let buttonWrapper = null;

        const footer = document.createElement("div");
        footer.className = "g-popup__footer";

        if (this.options.footerContent)
        {
            const footerContent = document.createElement("div");
            footerContent.className = "g-popup__footer-content";

            if (typeof this.options.footerContent === "string")
                footerContent.innerHTML = this.options.footerContent;
            else if (this.options.content instanceof HTMLElement)
                footerContent.appendChild(this.options.footerContent);

            footer.appendChild(footerContent);
        }

        //const scrollTopButton = document.createElement("button");

        //this.scrollTopEl = scrollTopButton;
        //footer.prepend(this.scrollTopEl);

        if (this.buttons)
        {
            if (this.options.customButtonContainer)
            {
                buttonWrapper = this.container.querySelector(this.options.customButtonContainer);
            }
            else
            {
                buttonWrapper = document.createElement("div");
                buttonWrapper.classList.add("g-popup__footer-buttons");
            }

            this.buttons?.forEach(btn =>
            {
                let button = null;

                if (btn.isAnchor)
                {
                    button = document.createElement("a");
                    button.setAttribute("href", btn.url);
                }
                else
                    button = document.createElement("button");

                button.textContent = btn.label;
                if (btn.className)
                    button.className = btn.className;

                if (btn.isNextButton)
                {
                    this.nextButtonEl = button;
                    if (btn.onClick)
                        button.addEventListener("click", btn.onClick);
                    else
                        button.addEventListener("click", () => instance.nextTab());
                }
                else if (btn.closeOnCLick)
                {
                    button.addEventListener("click", (e) =>
                    {
                        if (btn.onClick)
                            btn.onClick(e);

                        instance.hide();
                    });
                }
                else if (btn.onClick)
                    button.addEventListener("click", btn.onClick);

                buttonWrapper.appendChild(button);
            });
        }

        if (buttonWrapper)
        {
            if (this.buttonHolder)
                this.buttonHolder.replaceWith(buttonWrapper);
            else
            {
                footer.appendChild(buttonWrapper);
                this.buttonHolder = buttonWrapper;
            }
        }

        this.container.appendChild(footer);
    }

    public goToTab(index: number, animate: boolean = false)
    {
        if (!Array.isArray(this.options.content))
            return;

        const max = this.options.content.length - 1;
        const clamped = Math.max(0, Math.min(index, max));

        const previous = this.activeTab;
        this.activeTab = clamped;

        const shouldAnimate = animate && Math.abs(previous - clamped) === 1;

        this.updateSlide(shouldAnimate);
        this.updateUI();
    }

    public hideBackButton()
    {
        const backButton = document.getElementById(`modal-${this.options.id}`).querySelector(".g-popup__backbutton");
        backButton.remove();
    }

    public showBackButton(backButton: PopupButton)
    {
        const header = document.getElementById(`modal-${this.options.id}`).querySelector(".g-popup__header");

        this.backButtonEl = null;

        if (backButton.isAnchor)
        {
            this.backButtonEl = document.createElement("a");
            this.backButtonEl.setAttribute("href", backButton.url);
        }
        else
            this.backButtonEl = document.createElement("button");

        if (backButton.label)
        {
            this.backButtonEl.innerText = backButton.label;
            this.backButtonEl.title = backButton.label;
        }

        if (backButton.className)
            this.backButtonEl.className = backButton.className;
        else
        {
            this.backButtonEl.classList.add("g-popup__backbutton");
        }
        if (backButton.onClick)
            this.backButtonEl.addEventListener("click", backButton.onClick);
        else
            this.backButtonEl.addEventListener("click", () => this.previousTab());
        
        header.prepend(this.backButtonEl);
    }

    public nextTab(index?: number)
    {
        const target = typeof index === "number"
            ? index
            : this.activeTab + 1;

        const animate = typeof index === "number"
            ? Math.abs(target - this.activeTab) === 1
            : true;

        this.goToTab(target, animate);
    }

    public previousTab(index?: number)
    {
        const target = typeof index === "number"
            ? index
            : this.activeTab - 1;

        const animate = typeof index === "number"
            ? Math.abs(target - this.activeTab) === 1
            : true;

        this.goToTab(target, animate);
    }

    public getContent(): HTMLElement
    {
        return this.contentHolder;
    }

    public updateContent(content: any)
    {
        this.contentHolder.querySelector(".g-popup__content").innerHTML = content;
    }

    public updateTitle(title: any)
    {
        this.container.querySelector(".g-popup__title").innerHTML = title;
    }

    public destroy()
    {
        this.root.classList.remove("g-popup--visible");
        this.root.classList.remove("g-popup--is-active");
        document.documentElement.classList.remove("u-popup--active");
        this.root.remove();
    }

    private initEvents(): void
    {
        const instance = this;
        this.overlay.addEventListener('click', (e) =>
        {
            instance.hide();
        });

        // Toetsenbord events luisteraar
        document.addEventListener('keydown', (e: KeyboardEvent) =>
        {
            if (!instance.root.classList.contains('g-popup--visible'))
            {
                return;
            }

            // ESC-toets om te sluiten
            if (e.key === 'Escape')
            {
                instance.hide();
            }

            // Focus trap logica (Tab & Shift+Tab)
            if (e.key === 'Tab')
            {
                // 1. Fetch all elements that can currently receive focus
                const selectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
                const focusableElements = Array.from(this.container.querySelectorAll(selectors)) as HTMLElement[];

                // If there are no focusable elements, prevent tab actions entirely
                if (focusableElements.length === 0)
                {
                    e.preventDefault();
                    return;
                }

                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                // 2. Handle SHIFT + TAB (Backward Navigation)
                if (e.shiftKey)
                {
                    // If focusing the first element, or the container itself (initial open), loop to the last element
                    if (document.activeElement === firstElement || document.activeElement === this.container)
                    {
                        lastElement.focus();
                        e.preventDefault();
                    }
                }
                // 3. Handle TAB (Forward Navigation)
                else
                {
                    // If focusing the last element, loop back to the first element
                    if (document.activeElement === lastElement)
                    {
                        firstElement.focus();
                        e.preventDefault();
                    }
                }

                // 4. Fallback for radiobutton groups and native browser quirks
                // Defer execution until the browser has finished moving the focus
                setTimeout(() =>
                {
                    // If the focus managed to escape the popup container, force it back inside
                    if (!this.container.contains(document.activeElement))
                    {
                        if (e.shiftKey)
                        {
                            lastElement.focus(); // Direct back to the end if navigating backwards
                        } else
                        {
                            firstElement.focus(); // Direct back to the beginning if navigating forwards
                        }
                    }
                }, 0);
            }
        });
    }
}

(window as any).Popup = Popup;

const customRadioClick = (element: HTMLInputElement, className: string) =>
{
    const allElements = element.parentNode.parentNode.querySelectorAll(".c-popup__radio-holder");

    allElements.forEach((element) =>
    {
        element.classList.remove("c-popup__radio-holder--active");
    })

    const inputHolder = element.parentNode as HTMLDivElement;
    inputHolder.classList.add("c-popup__radio-holder--active");
}